import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { LgConsole } from "@logex/framework/core";
import {
    BrowserCacheProvider,
    LgUserflowService,
    LG_USERFLOW_SERVICE,
    ServerGatewayBase
} from "@logex/framework/lg-application";
import { AuthorizationPermission, NameId } from "@mrdm-self-service-frontend/shared/types";
import { SharedConfigService } from "@mrdm-self-service-frontend/shared/util-logex-framework-setup";
import { Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { AdministrationPermissions } from "./administration-permissions";
import { SharedUserAuthorizationService } from "@mrdm-self-service-frontend/shared/data-access-authorization";

@Injectable({
    providedIn: "root"
})
export class OrganizationPermissionService extends ServerGatewayBase {
    private _sharedAuthorization = inject(SharedUserAuthorizationService);
    private _userFlowService = inject<LgUserflowService>(LG_USERFLOW_SERVICE);

    public _permissions: AuthorizationPermission[] = [];

    public canRead = false;
    public canEdit = false;
    public isAdmin = false;

    readonly allowedOrganizations$: Observable<NameId[]>;

    constructor() {
        const _configService = inject(SharedConfigService);

        super();
        const apiUrl = _configService.applicationUrl;
        if (apiUrl != null) {
            this._setBaseUrl(apiUrl);
        } else {
            console.error("Cannot set application base API url based on configuration.");
        }
        this.allowedOrganizations$ = this.getAllowedOrganizations().pipe(shareReplay(1));

        this._sharedAuthorization.currentOrganizationPermissions$.subscribe(permissions => {
            this._permissions = permissions;
            this._setPermissionShortcuts();
        });
    }

    getAllowedOrganizations(): Observable<NameId[]> {
        return this._get<{ organizations: NameId[] }>(`/api/v1/currentUser/organizations`).pipe(
            map(data => data.organizations)
        );
    }

    public hasPermission(expectedPermissions: string[]): boolean {
        if (expectedPermissions == null) return true;

        for (const permission of expectedPermissions) {
            // have required permission for at least one product
            if (
                this._permissions.some(
                    authorizationPermission =>
                        authorizationPermission.permissions.indexOf(permission) !== -1
                )
            ) {
                return true;
            }
        }

        return false;
    }

    private _setPermissionShortcuts(): void {
        this.canEdit = this._canEdit;
        this.canRead = this._canRead;
        this.isAdmin = this._isAdmin;

        this._userFlowService.patchUserAttrs({ ssp_admin: this.isAdmin });
    }

    private get _canEdit(): boolean {
        return this.hasPermission([AdministrationPermissions.edit]);
    }

    private get _canRead(): boolean {
        return this.hasPermission([AdministrationPermissions.read, AdministrationPermissions.edit]);
    }

    private get _isAdmin(): boolean {
        return this.hasPermission([AdministrationPermissions.admin]);
    }
}
